// src/pages/admin/AdminDealsManagement.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiAlertCircle,
  FiToggleLeft,
  FiToggleRight,
  FiEyeOff,
  FiCopy,
  FiCheckSquare,
  FiSquare,
  FiUsers,
  FiXOctagon,
  FiAlertTriangle,
  FiStar
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { getDealImageUrl } from '../../utils/dealImageHelper';

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (v) => {
  try {
    return safeNumber(v).toLocaleString();
  } catch {
    return String(v || 0);
  }
};

const safeDate = (d) => {
  try {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('fr-FR');
  } catch {
    return '—';
  }
};

const computeStatusFromDates = (deal) => {
  try {
    const now = Date.now();
    const start = deal?.startDate ? new Date(deal.startDate).getTime() : null;
    const end = deal?.endDate ? new Date(deal.endDate).getTime() : null;

    if (end && end < now) return 'expired';
    // 'scheduled', PAS 'pending' : une annonce dont la date de début est future
    // est programmée, elle n'attend pas une validation. Les deux états étaient
    // confondus sous le même libellé « En attente », ce qui laissait croire
    // qu'une annonce déjà validée restait à approuver.
    if (start && start > now) return 'scheduled';

    const qty =
      typeof deal?.quantity === 'number'
        ? deal.quantity
        : safeNumber(deal?.quantity);
    const sold =
      typeof deal?.quantitySold === 'number'
        ? deal.quantitySold
        : safeNumber(deal?.quantitySold);

    if (qty !== -1 && qty > 0 && sold >= qty) {
      return 'sold_out';
    }

    return deal?.status || 'active';
  } catch {
    return deal?.status || 'active';
  }
};

// Statut réellement affiché pour un deal.
// Le statut STOCKÉ fait foi, SAUF s'il dit "sold_out" alors qu'il reste du stock :
// ce cas correspond à un deal réapprovisionné dont le statut n'a pas encore été
// resynchronisé côté serveur (ex: 4 vendus / 7 affiché "Épuisé"). On corrige donc
// l'affichage immédiatement, sans attendre le passage du job de synchronisation.
const resolveDealStatus = (deal) => {
  const stored = deal?.status;
  // Validée mais pas encore commencée -> « Programmé ». Elle est bien `active`
  // en base, mais reste absente des listes publiques tant que la date de début
  // n'est pas atteinte : l'afficher « Actif » serait trompeur.
  if (stored === 'active') {
    const start = deal?.startDate ? new Date(deal.startDate).getTime() : null;
    if (start && start > Date.now()) return 'scheduled';
  }
  if (stored === 'sold_out') {
    const qty =
      typeof deal?.quantity === 'number' ? deal.quantity : safeNumber(deal?.quantity);
    const sold =
      typeof deal?.quantitySold === 'number' ? deal.quantitySold : safeNumber(deal?.quantitySold);
    // stock illimité (<= 0 ou -1) OU il reste des unités -> le deal n'est PAS épuisé
    if (qty === -1 || qty <= 0 || sold < qty) return computeStatusFromDates({ ...deal, status: 'active' });
  }
  return stored || computeStatusFromDates(deal);
};

const getStatusBadge = (status) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    scheduled: 'bg-blue-100 text-blue-800',
    expired: 'bg-red-100 text-red-800',
    sold_out: 'bg-orange-100 text-orange-800',
    draft: 'bg-gray-100 text-gray-800',
    inactive: 'bg-gray-100 text-gray-800'
  };
  const labels = {
    active: 'Actif',
    pending: 'En attente de validation',
    scheduled: 'Programmé',
    expired: 'Expiré',
    sold_out: 'Épuisé',
    draft: 'Brouillon',
    inactive: 'Inactif'
  };
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        styles[status] || styles.draft
      }`}
    >
      {labels[status] || status || '—'}
    </span>
  );
};

const resolveMerchantName = (deal) => {
  if (!deal) return 'N/A';
  const m = deal.merchantId || deal.merchant || deal.merchantData || null;
  if (!m) return 'N/A';
  if (typeof m === 'string') return m;
  if (typeof m === 'object') {
    return (
      m.businessName ||
      m.name ||
      m.companyName ||
      m.company ||
      (m._id ? String(m._id) : 'N/A')
    );
  }
  return 'N/A';
};

const AdminDealsManagement = () => {
  const [deals, setDeals] = useState([]);
  const toggleDealEnabled = async (deal) => {
    const id = deal._id || deal.id;
    if (!id) return;
    const cur = deal.enabled !== false;
    const apply = (val) => (d) => ((d._id || d.id) === id ? { ...d, enabled: val } : d);
    setDeals((prev) => prev.map(apply(!cur)));
        try {
      const res = await API.patch(`/deals/${id}/toggle`);
      const next = res && res.data && typeof res.data.enabled === 'boolean' ? res.data.enabled : !cur;
      setDeals((prev) => prev.map(apply(next)));
            toast.success((res && res.data && res.data.message) || (next ? 'Deal activé' : 'Deal désactivé'));
    } catch (e) {
      setDeals((prev) => prev.map(apply(cur)));
            toast.error('Action impossible');
    }
  };

  // COUP DE COEUR — remonte l'annonce en tete de toutes les listes publiques.
  //
  // L'etat est inverse LOCALEMENT AVANT l'appel : sur une liste de 300 annonces,
  // attendre la reponse du serveur donne l'impression que le clic n'a pas
  // fonctionne, et l'admin reclique. En cas d'echec on remet la valeur d'avant.
  const toggleFeatured = async (deal) => {
    const id = deal._id || deal.id;
    if (!id) return;
    const cur = !!deal.featured;
    const apply = (val) => (d) => ((d._id || d.id) === id ? { ...d, featured: val } : d);
    setDeals((prev) => prev.map(apply(!cur)));
    try {
      const res = await API.patch(`/admin/deals/${id}/toggle-featured`);
      // La route renvoie l'annonce complete dans `data`. On lit la valeur
      // REELLE plutot que de supposer l'inverse de l'ancienne : le serveur peut
      // refuser (quota de mises en avant du pack) et renvoyer l'etat inchange.
      const next = typeof res?.data?.data?.featured === 'boolean'
        ? res.data.data.featured
        : (typeof res?.data?.featured === 'boolean' ? res.data.featured : !cur);
      setDeals((prev) => prev.map(apply(next)));
      toast.success(res?.data?.message || (next ? 'Ajouté aux coups de cœur' : 'Retiré des coups de cœur'));
    } catch (e) {
      setDeals((prev) => prev.map(apply(cur)));
      toast.error(e?.response?.data?.message || 'Action impossible');
    }
  };

  // ============================================================
  // v1.418.0 — POIDS D'EXPOSITION DANS LA ROTATION DE L'ACCUEIL
  //
  // 1 = passage normal, 2 = deux fois plus souvent a l'ecran, jusqu'a 5.
  // Le serveur plafonne le poids reel pour qu'une meme offre ne puisse jamais
  // occuper deux emplacements du meme lot ; un poids 5 sur trois offres n'a
  // donc pas plus d'effet qu'un poids 1 — c'est voulu, pas un bug.
  //
  // Le poids n'a de sens que si l'offre est mise en avant : regler un poids
  // sur une offre sans etoile ne l'affichera pas pour autant.
  // ============================================================
  // ============================================================
  // v1.421.0 — SAISIE DIRECTE DU PRIX PARTENAIRE
  // Apres le correctif v1.420.0, les prix saisis auparavant n'ont jamais ete
  // ecrits en base : il faut les ressaisir. Le faire annonce par annonce
  // (ouvrir, saisir, enregistrer, revenir) est intenable sur des dizaines de
  // lignes — d'ou la saisie directe dans la liste.
  // ============================================================
  const [ppEditing, setPpEditing] = useState(null);   // id en cours d'edition
  // v1.451.0 — bientôt expirés + date de fin modifiable
  const [serverStats, setServerStats] = useState(null);
  const [expiringFilter, setExpiringFilter] = useState(0); // 0 = inactif, 15 = <15 jours
  const [edEditing, setEdEditing] = useState(null);
  const [edValue, setEdValue] = useState('');
  const [edSaving, setEdSaving] = useState(null);
  const [ppValue, setPpValue] = useState('');
  const [ppSaving, setPpSaving] = useState(null);

  const openPartnerPrice = (deal) => {
    const id = String(deal._id || deal.id);
    setPpEditing(id);
    setPpValue(deal.partnerPrice === null || deal.partnerPrice === undefined ? '' : String(deal.partnerPrice));
  };

  const savePartnerPrice = async (deal) => {
    const id = deal._id || deal.id;
    try {
      setPpSaving(String(id));
      const res = await API.patch(`/admin/deals/${id}/partner-price`, { partnerPrice: ppValue });
      const stored = res?.data?.data?.partnerPrice ?? null;
      // On affiche la valeur RELUE par le serveur, pas celle tapee : si elle
      // n'a pas ete conservee, l'ecran ne doit pas faire croire au contraire.
      setDeals((prev) => prev.map((d) =>
        (d._id || d.id) === id ? { ...d, partnerPrice: stored } : d));
      setPpEditing(null);
      toast.success(res?.data?.message || 'Prix partenaire enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Enregistrement impossible');
    } finally { setPpSaving(null); }
  };

  // ============================================================
  // v1.451.0 — PROLONGER UNE ANNONCE DEPUIS LA LISTE
  // Sans cela, prolonger 30 annonces en fin de mois demandait 30 allers-retours
  // par le formulaire complet d'édition.
  // ============================================================
  const openEndDate = (deal) => {
    const id = String(deal._id || deal.id);
    setEdEditing(id);
    // <input type="date"> exige AAAA-MM-JJ : toute autre forme laisse le champ
    // vide sans le moindre message.
    const d = deal.endDate ? new Date(deal.endDate) : null;
    setEdValue(d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '');
  };

  const saveEndDate = async (deal) => {
    const id = deal._id || deal.id;
    try {
      setEdSaving(String(id));
      const res = await API.patch(`/admin/deals/${id}/end-date`, { endDate: edValue });
      const d = res?.data?.data || {};
      setDeals((prev) => prev.map((x) =>
        (x._id || x.id) === id ? { ...x, endDate: d.endDate ?? null, status: d.status || x.status } : x));
      setEdEditing(null);
      toast.success(res?.data?.message || 'Date mise à jour');
      // Le compteur « Bientôt expirés » doit suivre : on recharge.
      fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter, expiringFilter);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Modification impossible');
    } finally { setEdSaving(null); }
  };

  const setFeaturedWeight = async (deal, weight) => {
    const id = deal._id || deal.id;
    if (!id) return;
    const cur = Number(deal.featuredWeight) || 1;
    const w = Number(weight) || 1;
    if (w === cur) return;
    const apply = (val) => (d) => ((d._id || d.id) === id ? { ...d, featuredWeight: val } : d);
    setDeals((prev) => prev.map(apply(w)));
    try {
      await API.patch(`/admin/deals/${id}/featured-config`, { featuredWeight: w });
      toast.success(w === 1 ? 'Exposition normale' : `Exposition ×${w}`);
    } catch (e) {
      setDeals((prev) => prev.map(apply(cur)));
      toast.error(e?.response?.data?.message || 'Réglage impossible');
    }
  };

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pré-remplit la recherche depuis l'URL (?q= ou ?deal=) — clic depuis le dashboard
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const q = searchParams.get('q') || searchParams.get('deal');
    if (q) setSearchTerm(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [statusFilter, setStatusFilter] = useState('all');
  // Filtre ventes : '' = tout, '1' = a deja vendu, '0' = jamais vendu.
  const [soldFilter, setSoldFilter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---------------------------------------------------------------
  // Moderation en lot.
  // Valider une par une est ingerable des qu'un marchand depose 30
  // annonces : on filtre par marchand, on coche, on valide en une fois.
  // `merchantOptions` vient de /admin/deals/moderation/merchants et
  // porte le nombre d'annonces EN ATTENTE par marchand — c'est ce qui
  // permet de voir qui est a traiter sans ouvrir chaque fiche.
  // ---------------------------------------------------------------
  const [merchantFilter, setMerchantFilter] = useState('');
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { scope, count, label }
  const [bulkReport, setBulkReport] = useState(null);   // resultat detaille (ignores)
  const [bulkReject, setBulkReject] = useState(null);   // { scope, count, label }
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  // Publication en force : l'admin a le dernier mot. Quand le serveur bloque
  // (marchand dont l'email n'est pas confirmé, KYC non validé), on n'affiche
  // pas un refus sec : on montre le motif et on propose de publier quand même.
  const [forceConfirm, setForceConfirm] = useState(null); // { id, title, reasons }

  // Refus d'une annonce : le motif est obligatoire et transmis au marchand.
  const [duplicatingId, setDuplicatingId] = useState(null);

  const [rejectDeal, setRejectDeal] = useState(null); // { id, title }
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleteTitle, setDeleteTitle] = useState('');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const mountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter, expiringFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter, expiringFilter]);

  useEffect(() => {
    fetchMerchantOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Liste des marchands ayant des annonces + compteur par statut.
  // Volontairement non bloquante : si l'appel echoue, le filtre est simplement
  // absent, la page reste utilisable.
  const fetchMerchantOptions = async () => {
    try {
      const res = await API.get('/admin/deals/moderation/merchants');
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      if (mountedRef.current) setMerchantOptions(list);
    } catch (e) {
      if (mountedRef.current) setMerchantOptions([]);
    }
  };

  const fetchDeals = async (
    pageToFetch = 1,
    limitToFetch = 20,
    q = '',
    status = 'all',
    merchant = '',
    sold = '',
    expiring = 0
  ) => {
    setLoading(true);
    try {
      const params = { page: pageToFetch, limit: limitToFetch };
      if (q) params.q = q;
      if (status && status !== 'all') params.status = status;
      if (merchant) params.merchantId = merchant;
      // sold === '0' est une valeur VALIDE (jamais vendu) : tester la
      // chaine vide et non la valeur falsy, sinon '0' serait ignore.
      if (sold !== '') { params.sold = sold; params.sort = 'sold'; }
      // v1.451.0 — bientôt expirés
      if (expiring > 0) params.expiring = expiring;

      const res = await API.get('/admin/deals', { params });
      const payload = res?.data ?? {};
      // Compteurs calculés par le serveur sur toute la base.
      if (payload.stats) setServerStats(payload.stats);

      let list = [];
      if (Array.isArray(payload)) list = payload;
      else if (Array.isArray(payload.data)) list = payload.data;
      else if (Array.isArray(payload.docs)) list = payload.docs;
      else if (Array.isArray(payload.data?.data)) list = payload.data.data;
      else if (Array.isArray(payload.result)) list = payload.result;
      else if (Array.isArray(payload.items)) list = payload.items;

      const normalized = (list || []).map((d) => ({
        ...d,
        startDate: d.startDate || d.validFrom || null,
        endDate: d.endDate || d.validUntil || null
      }));

      if (!mountedRef.current) return;
      setDeals(normalized);
      // La selection ne survit pas a un changement de page ou de filtre :
      // valider des ids qui ne sont plus a l'ecran serait une action a l'aveugle.
      setSelectedIds([]);

      const pageFromServer =
        payload.currentPage ??
        payload.page ??
        payload.data?.currentPage ??
        payload.meta?.currentPage ??
        null;
      const totalPagesPayload =
        payload.totalPages ??
        payload.meta?.totalPages ??
        payload.pagination?.totalPages ??
        null;
      const totalItems =
        payload.totalItems ??
        payload.total ??
        payload.meta?.total ??
        payload.pagination?.totalItems ??
        payload.pagination?.total ??
        null;
      const itemsPerPage =
        payload.itemsPerPage ??
        payload.limit ??
        payload.meta?.limit ??
        payload.pagination?.itemsPerPage ??
        limitToFetch;

      if (pageFromServer) setPage(Number(pageFromServer));
      if (itemsPerPage) setPerPage(Number(itemsPerPage));
      if (totalItems) {
        setTotal(Number(totalItems));
      } else {
        const headerTotal = res?.headers?.['x-total-count'];
        if (headerTotal) setTotal(Number(headerTotal));
        else setTotal(normalized.length || 0);
      }

      if (totalPagesPayload && pageToFetch > Number(totalPagesPayload)) {
        setPage(Number(totalPagesPayload));
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des deals');
      if (mountedRef.current) setDeals([]);
      setTotal(0);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const askDelete = (deal) => {
    const id = deal?._id || deal?.id;
    if (!id) {
      toast.error('ID du deal manquant');
      return;
    }
    setDeleteId(id);
    setDeleteTitle(deal?.title || deal?.name || 'élément');
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoadingDelete(true);

    try {
      await API.delete(`/admin/deals/${deleteId}`);
      toast.success('Deal supprimé avec succès');
      setShowDelete(false);
      setDeleteId(null);
      setDeleteTitle('');
      await fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erreur lors de la suppression'
      );
    } finally {
      if (mountedRef.current) setLoadingDelete(false);
    }
  };

  const handleApprove = async (dealId, { force = false, title = '' } = {}) => {
    if (!dealId) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await API.put(`/admin/deals/${dealId}/approve`, force ? { force: true } : {});
      const d = res?.data || {};
      toast.success(d.message || 'Annonce validée et publiée');
      setForceConfirm(null);
      await fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter);
      await fetchMerchantOptions();
    } catch (error) {
      const data = error?.response?.data || {};
      // Le serveur signale un blocage surmontable : on propose la publication
      // en force au lieu d'un simple message d'erreur.
      if (data.canForce) {
        setForceConfirm({
          id: dealId,
          title: title || 'cette annonce',
          reasons: Array.isArray(data.reasons) ? data.reasons : [data.message]
        });
      } else {
        toast.error(data.message || "Erreur lors de l'approbation");
      }
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  // Duplication : évite de recréer à zéro 5 annonces qui ne diffèrent que par
  // le prix ou une photo. La copie part en attente de validation.
  const handleDuplicate = async (deal) => {
    const id = deal?._id || deal?.id;
    if (!id || duplicatingId) return;
    setDuplicatingId(id);
    try {
      const res = await API.post(`/admin/deals/${id}/duplicate`);
      const copyId = res?.data?.data?._id || res?.data?.data?.id;
      toast.success(res?.data?.message || 'Annonce dupliquée');
      // L'admin édite via /merchant/deals/:id/edit en mode admin (cf. openEdit) :
      // il n'existe pas de route /admin/deals/:id/edit.
      if (copyId) navigate(`/merchant/deals/${copyId}/edit`, { state: { adminMode: true } });
      else await fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la duplication');
    } finally {
      if (mountedRef.current) setDuplicatingId(null);
    }
  };

  const askReject = (deal) => {
    const id = deal?._id || deal?.id;
    if (!id) return toast.error('ID du deal manquant');
    setRejectDeal({ id, title: deal?.title || 'Sans titre' });
    setRejectReason('');
  };

  const handleReject = async () => {
    if (!rejectDeal?.id) return;
    if (rejectReason.trim().length < 5) {
      return toast.error('Motif obligatoire (5 caractères minimum) — il est envoyé au marchand');
    }
    try {
      setRejecting(true);
      await API.put(`/admin/deals/${rejectDeal.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Annonce refusée — le marchand a été notifié du motif');
      setRejectDeal(null);
      setRejectReason('');
      await fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors du refus');
    } finally {
      if (mountedRef.current) setRejecting(false);
    }
  };

  // ---------------------------------------------------------------
  // Selection : seules les annonces reellement moderables sont cochables
  // (en attente ou refusees). Le statut STOCKE fait foi ici, pas le statut
  // affiche : une annonce validee mais programmee n'est pas a valider.
  // ---------------------------------------------------------------
  const isSelectable = (deal) => {
    const stored = deal?.status;
    return stored === 'pending' || stored === 'rejected';
  };

  const selectableIds = useMemo(
    () => deals.filter(isSelectable).map((d) => String(d._id || d.id)),
    [deals]
  );

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : selectableIds);
  };

  // Index marchand -> etat de verification, pour signaler AVANT le clic les
  // annonces qui demanderont une publication en force.
  const merchantStateById = useMemo(() => {
    const m = new Map();
    merchantOptions.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [merchantOptions]);

  const dealMerchantId = (deal) => {
    const m = deal?.merchant || deal?.merchantId;
    if (!m) return null;
    if (typeof m === 'object') return String(m.id || m._id || '');
    return String(m);
  };

  const merchantNeedsForce = (deal) => {
    const st = merchantStateById.get(dealMerchantId(deal));
    if (!st) return false;
    return st.ownerEmailVerified === false || st.merchantVerified === false;
  };

  const currentMerchantOption = useMemo(
    () => merchantOptions.find((m) => String(m.id) === String(merchantFilter)) || null,
    [merchantOptions, merchantFilter]
  );

  const pendingTotalAll = useMemo(
    () => merchantOptions.reduce((sum, m) => sum + (Number(m.pending) || 0), 0),
    [merchantOptions]
  );

  const refreshAfterBulk = async () => {
    await fetchDeals(page, perPage, debouncedSearch, statusFilter, merchantFilter, soldFilter);
    await fetchMerchantOptions();
  };

  // scope : 'selection' (ids coches) | 'merchant' (file d'attente du marchand
  // filtre) | 'all' (toute la file d'attente).
  // scope 'forced' : relance uniquement les annonces bloquées du dernier
  // rapport, cette fois en passant outre les garde-fous.
  const runBulkApprove = async (scope, options = {}) => {
    if (bulkBusy) return;
    const force = options.force === true;
    let payload;
    if (scope === 'forced') {
      const ids = (options.ids || []).map(String);
      if (!ids.length) return;
      payload = { ids, force: true };
    } else {
      payload = scope === 'selection' ? { ids: selectedIds } : { all: true };
      if (scope === 'merchant') payload.merchantId = merchantFilter;
      if (force) payload.force = true;
    }
    if (scope === 'selection' && !selectedIds.length) {
      return toast.error('Aucune annonce sélectionnée');
    }

    setBulkBusy(true);
    try {
      const res = await API.put('/admin/deals/bulk-approve', payload);
      const d = res?.data || {};
      if (d.approved > 0) toast.success(d.message || `${d.approved} annonce(s) validée(s)`);
      else toast.warn(d.message || 'Aucune annonce validée');
      setBulkReport({
        mode: 'approve',
        done: Number(d.approved) || 0,
        skipped: Array.isArray(d.skipped) ? d.skipped : [],
        forced: Number(d.forced) || 0,
        bypassed: Array.isArray(d.bypassed) ? d.bypassed : [],
        remaining: Number(d.remaining) || 0
      });
      setSelectedIds([]);
      setBulkConfirm(null);
      await refreshAfterBulk();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la validation en lot');
    } finally {
      if (mountedRef.current) setBulkBusy(false);
    }
  };

  const runBulkReject = async () => {
    if (bulkBusy || !bulkReject) return;
    const reason = bulkRejectReason.trim();
    if (reason.length < 5) {
      return toast.error('Motif obligatoire (5 caractères minimum) — il est envoyé au marchand');
    }
    const payload =
      bulkReject.scope === 'selection'
        ? { ids: selectedIds, reason }
        : { all: true, reason };
    if (bulkReject.scope === 'merchant') payload.merchantId = merchantFilter;

    setBulkBusy(true);
    try {
      const res = await API.put('/admin/deals/bulk-reject', payload);
      const d = res?.data || {};
      if (d.rejected > 0) toast.success(d.message || `${d.rejected} annonce(s) refusée(s)`);
      else toast.warn(d.message || 'Aucune annonce refusée');
      setBulkReport({
        mode: 'reject',
        done: Number(d.rejected) || 0,
        skipped: Array.isArray(d.skipped) ? d.skipped : [],
        remaining: Number(d.remaining) || 0
      });
      setSelectedIds([]);
      setBulkReject(null);
      setBulkRejectReason('');
      await refreshAfterBulk();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors du refus en lot');
    } finally {
      if (mountedRef.current) setBulkBusy(false);
    }
  };

  const openEdit = async (deal) => {
    const id = deal?._id || deal?.id;
    if (!id) {
      toast.error('ID du deal manquant');
      return;
    }

    try {
      let res;
      try {
        res = await API.get(`/admin/deals/${id}`);
      } catch (_) {
        try {
          res = await API.get(`/deals/id/${id}`);
        } catch (_) {
          try {
            res = await API.get(`/merchant/deals/${id}`);
          } catch (_) {
            res = null;
          }
        }
      }

      if (res) {
        const payload = res?.data ?? {};
        const fullDeal = Array.isArray(payload)
          ? payload[0]
          : payload?.data ?? payload;
        if (fullDeal) {
          navigate(`/merchant/deals/${id}/edit`, {
            state: { deal: fullDeal, adminMode: true }
          });
          return;
        }
      }
    } catch (_) {}

    navigate(`/merchant/deals/${id}/edit`, { state: { deal, adminMode: true } });
  };

  const filteredDealsCount = deals.length;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || filteredDealsCount) / perPage)),
    [total, filteredDealsCount, perPage]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  const statsCount = useMemo(() => {
    // v1.451.0 — les compteurs viennent du SERVEUR, calculés sur toute la
    // base. Ils étaient auparavant comptés sur la page affichée : « Actifs 20 »
    // à côté de « Total 1 076 » voulait dire « 20 parmi les 50 affichées ».
    // Le calcul de page est conservé en repli tant que le serveur n'est pas
    // déployé.
    if (serverStats) {
      return {
        active: Number(serverStats.active) || 0,
        pending: Number(serverStats.pending) || 0,
        soldOut: Number(serverStats.soldOut) || 0,
        expired: Number(serverStats.expired) || 0,
        expiringSoon: Number(serverStats.expiringSoon) || 0,
        expiringDays: Number(serverStats.expiringDays) || 15,
      };
    }
    const activeCount = deals.filter((d) => {
      const s = resolveDealStatus(d);
      return s === 'active';
    }).length;

    const pendingCount = deals.filter((d) => {
      const s = resolveDealStatus(d);
      return s === 'pending';
    }).length;

    const soldOutCount = deals.filter((d) => {
      const s = resolveDealStatus(d);
      return s === 'sold_out';
    }).length;

    const expiredCount = deals.filter((d) => {
      const s = resolveDealStatus(d);
      return s === 'expired';
    }).length;

    return {
      active: activeCount,
      pending: pendingCount,
      soldOut: soldOutCount,
      expired: expiredCount
    };
  }, [deals, serverStats]);

  return (
    <div className="p-6">
      <ConfirmDeleteModal
        open={showDelete}
        title={`Supprimer : ${deleteTitle}`}
        description="Cette action supprimera définitivement ce deal. Êtes-vous certain(e) ?"
        onCancel={() => {
          if (!loadingDelete) {
            setShowDelete(false);
            setDeleteId(null);
            setDeleteTitle('');
          }
        }}
        onConfirm={confirmDelete}
        loading={loadingDelete}
      />

      {/* Refus d'une annonce : le motif est obligatoire et transmis au marchand,
          sinon il resoumet la même annonce sans savoir quoi corriger. */}
      {rejectDeal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Refuser l'annonce
            </h3>
            <p className="text-sm text-gray-500 mb-4 truncate">{rejectDeal.title}</p>

            <label className="block text-sm font-medium mb-1">
              Motif du refus <span className="text-red-500">*</span>
            </label>
            <textarea
              rows="4"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex : Les photos ne correspondent pas à la prestation / Texte inapproprié dans la description / Prix barré non justifié."
              className="w-full border rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Ce motif est envoyé au marchand dans ses notifications. L'annonce reste
              hors ligne tant qu'elle n'est pas corrigée et revalidée.
            </p>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => { if (!rejecting) { setRejectDeal(null); setRejectReason(''); } }}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                disabled={rejecting}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={rejecting || rejectReason.trim().length < 5}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {rejecting ? 'Refus en cours…' : 'Refuser et notifier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* L'admin garde le dernier mot : le blocage est explique, pas subi. */}
      {forceConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 inline-flex items-center gap-2">
              <FiAlertTriangle className="text-amber-600" />
              Marchand pas encore vérifié
            </h3>
            <p className="text-sm text-gray-500 mb-3 truncate">{forceConfirm.title}</p>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900 mb-1">
                Ce qui n'est pas encore validé :
              </p>
              <ul className="list-disc list-inside text-sm text-amber-900">
                {forceConfirm.reasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-gray-600 mt-3">
              La vérification du marchand est <strong>en cours</strong> — vous pouvez
              publier cette annonce dès maintenant sans l'attendre. La publication
              en force est enregistrée dans le journal d'audit à votre nom.
            </p>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => setForceConfirm(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleApprove(forceConfirm.id, { force: true, title: forceConfirm.title })}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-60"
              >
                {isSubmitting ? 'Publication…' : 'Publier quand même'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation avant une validation de masse : « tout valider » est
          irreversible cote public (les annonces partent en ligne). */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Valider {bulkConfirm.count} annonce(s) ?
            </h3>
            <p className="text-sm text-gray-600">
              Vous êtes sur le point de valider {bulkConfirm.label}. Elles seront
              publiées immédiatement.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Les annonces dont le marchand est encore en cours de vérification
              (email non confirmé, KYC en attente) seront listées à part dans le
              rapport, avec un bouton pour les <strong>publier quand même</strong>.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => setBulkConfirm(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => runBulkApprove(bulkConfirm.scope)}
                className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-60"
              >
                {bulkBusy ? 'Validation…' : 'Valider et publier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refus en lot : motif obligatoire, exactement comme un refus unitaire. */}
      {bulkReject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Refuser {bulkReject.count} annonce(s)
            </h3>
            <p className="text-sm text-gray-500 mb-4">{bulkReject.label}</p>

            <label className="block text-sm font-medium mb-1">
              Motif du refus <span className="text-red-500">*</span>
            </label>
            <textarea
              rows="4"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="Ex : Photos non conformes / Texte inapproprié / Prix barré non justifié."
              className="w-full border rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le même motif est envoyé à toutes les annonces refusées de ce lot.
            </p>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => { setBulkReject(null); setBulkRejectReason(''); }}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={bulkBusy || bulkRejectReason.trim().length < 5}
                onClick={runBulkReject}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
              >
                {bulkBusy ? 'Refus en cours…' : 'Refuser et notifier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rapport de lot : ce qui a ete ignore et POURQUOI. Sans ce detail,
          l'admin croit que la validation a echoue alors qu'un marchand n'est
          simplement pas encore valide. */}
      {bulkReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {bulkReport.mode === 'approve' ? 'Validation en lot' : 'Refus en lot'} — résultat
            </h3>
            <p className="text-sm text-gray-700">
              <strong>{bulkReport.done}</strong> annonce(s){' '}
              {bulkReport.mode === 'approve' ? 'validée(s) et publiée(s)' : 'refusée(s)'}.
              {bulkReport.remaining > 0 && (
                <>
                  {' '}Il reste <strong>{bulkReport.remaining}</strong> annonce(s) à traiter :
                  relancez l'action pour continuer.
                </>
              )}
            </p>

            {bulkReport.mode === 'approve' && bulkReport.forced > 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                {bulkReport.forced} annonce(s) publiée(s) malgré :{' '}
                {bulkReport.bypassed.join(' · ')}. Action enregistrée dans le
                journal d'audit.
              </p>
            )}

            {bulkReport.mode === 'approve' &&
              bulkReport.skipped.some((it) => it.canForce) && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm text-amber-900">
                    {bulkReport.skipped.filter((it) => it.canForce).length} annonce(s)
                    n'ont pas été publiées parce que leur marchand est encore en cours
                    de vérification. Vous pouvez passer outre.
                  </p>
                  <button
                    type="button"
                    disabled={bulkBusy}
                    onClick={() =>
                      runBulkApprove('forced', {
                        ids: bulkReport.skipped.filter((it) => it.canForce).map((it) => it.id)
                      })
                    }
                    className="mt-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <FiAlertTriangle />
                    {bulkBusy ? 'Publication…' : 'Publier quand même'}
                  </button>
                </div>
              )}

            {bulkReport.skipped.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  {bulkReport.skipped.length} annonce(s) ignorée(s) :
                </p>
                <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                  {bulkReport.skipped.map((it) => (
                    <div key={it.id} className="px-3 py-2 text-sm">
                      <div className="font-medium text-gray-900 truncate">
                        {it.title || `Annonce #${it.id}`}
                      </div>
                      <div className="text-xs text-red-700">{it.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-5">
              <button
                type="button"
                onClick={() => setBulkReport(null)}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestion des Deals
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez tous vos deals en un seul endroit
            </p>
          </div>
          <Link
            to="/merchant/deals/new"
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiPlus className="mr-2" />
            Créer un Deal
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher des deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Recherche des deals"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par statut"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="pending">En attente de validation</option>
              <option value="rejected">Refusé</option>
              <option value="expired">Expiré</option>
              <option value="sold_out">Épuisé</option>
              <option value="draft">Brouillon</option>
              <option value="inactive">Inactif</option>
            </select>

            {/* Filtre marchand : le compteur « en attente » evite d'ouvrir
                chaque fiche pour savoir qui est a traiter. */}
            <select
              value={merchantFilter}
              onChange={(e) => {
                setMerchantFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par marchand"
            >
              <option value="">Tous les marchands</option>
              {merchantOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.businessName}
                  {m.pending > 0 ? ` — ${m.pending} en attente` : ''}
                </option>
              ))}
            </select>

            {/* FILTRE VENTES. « Jamais vendu » est le plus utile des deux sur
                846 annonces : il isole ce qui ne part pas. Quand un filtre est
                actif, la liste est triee par ventes decroissantes. */}
            <select
              value={soldFilter}
              onChange={(e) => {
                setSoldFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Filtrer par ventes"
            >
              <option value="">Toutes les ventes</option>
              <option value="1">Deals vendus</option>
              <option value="0">Jamais vendus</option>
            </select>
          </div>

          {/* Actions de masse sur la file d'attente. Elles ne touchent QUE les
              annonces en attente : jamais une annonce refusee, expiree ou
              deja publiee. */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
            <span className="text-sm text-gray-600 inline-flex items-center gap-2">
              <FiUsers className="text-gray-400" />
              File d'attente :{' '}
              <strong className="text-gray-900">{pendingTotalAll}</strong> annonce(s)
            </span>

            {currentMerchantOption && (
              <button
                type="button"
                disabled={bulkBusy || !currentMerchantOption.pending}
                onClick={() =>
                  setBulkConfirm({
                    scope: 'merchant',
                    count: currentMerchantOption.pending,
                    label: `toutes les annonces en attente de « ${currentMerchantOption.businessName} »`
                  })
                }
                className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <FiCheckCircle />
                Valider les {currentMerchantOption.pending} annonce(s) de ce marchand
              </button>
            )}

            <button
              type="button"
              disabled={bulkBusy || !pendingTotalAll}
              onClick={() =>
                setBulkConfirm({
                  scope: 'all',
                  count: pendingTotalAll,
                  label: 'toutes les annonces en attente, tous marchands confondus'
                })
              }
              className="px-3 py-2 rounded-lg border border-green-600 text-green-700 text-sm font-semibold hover:bg-green-50 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FiCheckSquare />
              Tout valider ({pendingTotalAll})
            </button>
          </div>
        </div>
      </div>

      {/* v1.451.0 — vignettes CLIQUABLES : chacune applique son filtre.
          Un compteur qu'on lit sans pouvoir ouvrir la liste correspondante
          oblige à retrouver le filtre à la main dans les menus. */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Deals</p>
              <p className="text-2xl font-bold text-gray-900">
                {total || deals.length}
              </p>
            </div>
            <FiClock className="text-blue-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actifs</p>
              <p className="text-2xl font-bold text-green-600">
                {statsCount.active}
              </p>
            </div>
            <FiCheckCircle className="text-green-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">À valider</p>
              <p className="text-2xl font-bold text-yellow-600">
                {statsCount.pending}
              </p>
            </div>
            <FiClock className="text-yellow-500 text-2xl" />
          </div>
        </div>

        {/* BIENTOT EXPIRES — la seule vignette qui appelle une ACTION :
            prolonger avant que l'annonce ne disparaisse du site. */}
        <button
          type="button"
          onClick={() => {
            const actif = expiringFilter > 0;
            setExpiringFilter(actif ? 0 : (statsCount.expiringDays || 15));
            if (!actif) setStatusFilter('all');
            setPage(1);
          }}
          className={`text-left bg-white rounded-lg shadow p-4 transition border-2 ${
            expiringFilter > 0 ? 'border-amber-500 ring-2 ring-amber-100' : 'border-transparent hover:border-amber-300'
          }`}
          title="Afficher uniquement les annonces qui expirent bientôt"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                Bientôt expirés
                <span className="block text-[11px] text-gray-400">
                  moins de {statsCount.expiringDays || 15} jours
                </span>
              </p>
              <p className="text-2xl font-bold text-amber-600">
                {statsCount.expiringSoon ?? 0}
              </p>
            </div>
            <FiClock className="text-amber-500 text-2xl" />
          </div>
          {expiringFilter > 0 && (
            <span className="mt-1 inline-block text-[11px] font-semibold text-amber-700">
              Filtre actif — cliquer pour retirer
            </span>
          )}
        </button>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Épuisés</p>
              <p className="text-2xl font-bold text-orange-600">
                {statsCount.soldOut}
              </p>
            </div>
            <FiAlertCircle className="text-orange-500 text-2xl" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expirés</p>
              <p className="text-2xl font-bold text-red-600">
                {statsCount.expired}
              </p>
            </div>
            <FiXCircle className="text-red-500 text-2xl" />
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="sticky top-0 z-20 mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="text-sm font-semibold text-primary-900">
            {selectedIds.length} annonce(s) sélectionnée(s)
          </span>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => runBulkApprove('selection')}
            className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <FiCheckCircle />
            {bulkBusy ? 'Validation…' : 'Valider la sélection'}
          </button>
          <button
            type="button"
            disabled={bulkBusy}
            onClick={() => {
              setBulkRejectReason('');
              setBulkReject({
                scope: 'selection',
                count: selectedIds.length,
                label: `les ${selectedIds.length} annonce(s) sélectionnée(s)`
              });
            }}
            className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <FiXOctagon />
            Refuser la sélection
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-white"
          >
            Effacer la sélection
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={!selectableIds.length}
                    className="text-gray-500 hover:text-primary-600 disabled:opacity-30"
                    title={
                      selectableIds.length
                        ? 'Tout sélectionner sur cette page (annonces à valider uniquement)'
                        : 'Aucune annonce à valider sur cette page'
                    }
                    aria-label="Tout sélectionner"
                  >
                    {allSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marchand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix partenaire
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                    <p className="mt-2 text-gray-500">Chargement...</p>
                  </td>
                </tr>
              ) : deals.length > 0 ? (
                deals.map((deal) => {
                  const id = deal?._id || deal?.id;
                  const resolvedStatus =
                    resolveDealStatus(deal);
                  const merchantName = resolveMerchantName(deal);

                  const qty =
                    typeof deal.quantity === 'number'
                      ? deal.quantity
                      : safeNumber(deal.quantity);
                  const sold =
                    typeof deal.quantitySold === 'number'
                      ? deal.quantitySold
                      : safeNumber(deal.quantitySold);
                  const qtyDisplay =
                    qty === -1 ? 'Illimité' : `${sold} / ${qty}`;

                  const merchantIdValue =
                    deal?.merchantId?._id || deal?.merchantId || '—';

                  const discountPercent = safeNumber(
                    deal?.discountPercentage
                  );

                  return (
                    <tr
                      key={id}
                      className={`transition-colors ${
                        selectedIds.includes(String(id))
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        {isSelectable(deal) ? (
                          <button
                            type="button"
                            onClick={() => toggleSelect(id)}
                            className="text-gray-500 hover:text-primary-600"
                            aria-label={`Sélectionner ${deal?.title || 'annonce'}`}
                          >
                            {selectedIds.includes(String(id)) ? (
                              <FiCheckSquare size={18} className="text-primary-600" />
                            ) : (
                              <FiSquare size={18} />
                            )}
                          </button>
                        ) : (
                          <span className="inline-block w-[18px]" />
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={getDealImageUrl(deal, 0, '/placeholder.jpg')}
                            alt={deal?.title || 'deal'}
                            className="w-12 h-12 rounded-lg object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/placeholder.jpg';
                            }}
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                              {deal?.title || 'Sans titre'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {deal?.category || '-'}
                            </div>
                            {deal?.rejectionReason && (
                              <div className="mt-1 max-w-xs text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                                <strong>Motif du refus :</strong> {deal.rejectionReason}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {merchantName || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          id: {String(merchantIdValue)}
                        </div>
                        {merchantNeedsForce(deal) && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-amber-100 text-amber-800"
                            title="Vous pouvez publier quand même : la validation vous sera proposée."
                          >
                            <FiAlertTriangle size={11} />
                            Marchand non vérifié
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(deal?.discountedPrice)} MAD
                          </div>
                          <div className="text-gray-500 line-through">
                            {formatCurrency(deal?.originalPrice)} MAD
                          </div>
                          <div
                            className={`font-medium ${
                              discountPercent > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {discountPercent > 0
                              ? `-${discountPercent}%`
                              : `${discountPercent}%`}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {qtyDisplay}
                      </td>

                      {/* PRIX PARTENAIRE — cliquer sur la valeur pour la
                          modifier sans ouvrir l'annonce. Vide = commission. */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {ppEditing === String(id) ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number" min="0" step="0.01" autoFocus
                              value={ppValue}
                              onChange={(e) => setPpValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') savePartnerPrice(deal);
                                if (e.key === 'Escape') setPpEditing(null);
                              }}
                              placeholder="Vide = commission"
                              className="w-28 px-2 py-1 border border-amber-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-amber-400"
                            />
                            <button type="button" onClick={() => savePartnerPrice(deal)}
                              disabled={ppSaving === String(id)}
                              className="px-2 py-1 rounded bg-green-600 text-white text-xs font-semibold disabled:opacity-60">
                              {ppSaving === String(id) ? '…' : 'OK'}
                            </button>
                            <button type="button" onClick={() => setPpEditing(null)}
                              className="px-1.5 py-1 rounded border border-gray-300 text-xs text-gray-600">
                              ×
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openPartnerPrice(deal)}
                            title="Cliquer pour modifier"
                            className="text-left group"
                          >
                            {deal.partnerPrice === null || deal.partnerPrice === undefined ? (
                              // « Commission » et non « — » : l'absence de prix
                              // n'est pas une donnee manquante, c'est un mode de
                              // remuneration bien reel.
                              <span className="text-gray-400 group-hover:text-primary-600 italic">Commission</span>
                            ) : (
                              <span className="font-semibold text-amber-800 group-hover:text-amber-900">
                                {formatCurrency(deal.partnerPrice)} MAD
                              </span>
                            )}
                          </button>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {deal.enabled === false ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <FiEyeOff size={11} /> Désactivé
                          </span>
                        ) : getStatusBadge(resolvedStatus)}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>Début: {safeDate(deal?.startDate)}</div>
                        {edEditing === String(id) ? (
                          <div className="flex items-center gap-1 mt-1">
                            <input
                              type="date" autoFocus
                              value={edValue}
                              onChange={(e) => setEdValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEndDate(deal);
                                if (e.key === 'Escape') setEdEditing(null);
                              }}
                              className="border border-amber-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400"
                            />
                            <button type="button" onClick={() => saveEndDate(deal)}
                              disabled={edSaving === String(id)}
                              className="px-2 py-0.5 rounded bg-green-600 text-white text-xs font-semibold disabled:opacity-60">
                              {edSaving === String(id) ? '…' : 'OK'}
                            </button>
                            <button type="button" onClick={() => setEdEditing(null)}
                              className="px-1.5 py-0.5 rounded border border-gray-300 text-xs text-gray-600">×</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => openEndDate(deal)}
                            title="Cliquer pour modifier la date de fin"
                            className="text-left group">
                            Fin:{' '}
                            {/* Jours restants affichés à côté de la date : la
                                date seule oblige à calculer de tête pour savoir
                                s'il faut agir. */}
                            <span className={`group-hover:underline ${
                              deal.endDate && (new Date(deal.endDate) - Date.now()) / 86400000 <= 15
                                ? 'text-amber-700 font-semibold' : ''
                            }`}>
                              {safeDate(deal?.endDate) || '—'}
                            </span>
                            {deal.endDate && (() => {
                              const j = Math.ceil((new Date(deal.endDate) - Date.now()) / 86400000);
                              if (j < 0) return <span className="ml-1 text-red-600 font-semibold">(expiré)</span>;
                              if (j <= 15) return <span className="ml-1 text-amber-700 font-semibold">({j} j)</span>;
                              return null;
                            })()}
                          </button>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => toggleFeatured(deal)}
                            className={deal.featured ? 'text-amber-500 hover:text-amber-600' : 'text-gray-300 hover:text-amber-500'}
                            title={deal.featured ? 'Retirer des coups de cœur' : 'Mettre en coup de cœur (affiché en premier)'}
                            aria-label="Coup de cœur"
                          >
                            <FiStar size={18} fill={deal.featured ? 'currentColor' : 'none'} />
                          </button>

                          {/* Poids de rotation : n'apparait que sur les offres
                              mises en avant, sinon la colonne serait illisible
                              sur une liste de plusieurs centaines de lignes. */}
                          {deal.featured && (
                            <select
                              value={Number(deal.featuredWeight) || 1}
                              onChange={(e) => setFeaturedWeight(deal, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              title="Fréquence de passage sur la page d'accueil"
                              aria-label="Poids de rotation"
                              className="text-xs border border-amber-200 bg-amber-50 text-amber-700 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-300"
                            >
                              <option value={1}>×1</option>
                              <option value={2}>×2</option>
                              <option value={3}>×3</option>
                              <option value={4}>×4</option>
                              <option value={5}>×5</option>
                            </select>
                          )}

                          <Link
                            to={`/deals/${deal.slug || id}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="Voir sur le site"
                          >
                            <FiEye size={18} />
                          </Link>

                          <button
                            type="button"
                            onClick={() => handleDuplicate(deal)}
                            disabled={duplicatingId === id}
                            className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                            title="Dupliquer cette annonce"
                            aria-label="Dupliquer"
                          >
                            <FiCopy size={18} className={duplicatingId === id ? 'animate-pulse' : ''} />
                          </button>

                          <button
                            type="button"
                            onClick={() => toggleDealEnabled(deal)}
                            className={deal.enabled === false ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}
                            title={deal.enabled === false ? 'Activer le deal' : 'Désactiver le deal'}
                            aria-label={deal.enabled === false ? 'Activer' : 'Désactiver'}
                          >
                            {deal.enabled === false ? <FiToggleLeft size={18} /> : <FiToggleRight size={18} />}
                          </button>

                          {(resolvedStatus === 'pending' || resolvedStatus === 'rejected') && (
                            <button
                              type="button"
                              onClick={() => handleApprove(id, { title: deal?.title })}
                              className="text-green-600 hover:text-green-900"
                              title="Valider et publier"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiCheckCircle size={18} />
                            </button>
                          )}

                          {(resolvedStatus === 'pending' || resolvedStatus === 'active') && (
                            <button
                              type="button"
                              onClick={() => askReject(deal)}
                              className="text-red-600 hover:text-red-900"
                              title="Refuser (motif obligatoire)"
                              disabled={isSubmitting}
                              aria-disabled={isSubmitting}
                            >
                              <FiXCircle size={18} />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => openEdit(deal)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Modifier (admin)"
                          >
                            <FiEdit size={18} />
                          </button>

                          <button
                            type="button"
                            onClick={() => askDelete(deal)}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer"
                            disabled={isSubmitting || loadingDelete}
                            aria-disabled={isSubmitting || loadingDelete}
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <FiClock className="mx-auto text-4xl mb-2" />
                    <p>Aucun deal trouvé</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>

            <div className="ml-4 text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>

            <div className="ml-4 text-sm text-gray-600">
              <span className="mr-2">Afficher</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            {total > 0
              ? `Total: ${total} résultats`
              : `${deals.length} affichés`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDealsManagement;